import { createSlice } from "@reduxjs/toolkit";
import {
  RaceDataAllCars,
  CarRaceData,
  LapTable,
  DecodedEncryptionChannels,
} from "../types/raceData";

const initialState: RaceDataAllCars = {};

const createEmptyCarRaceData = (): CarRaceData => ({
  lapTable: {},
  ressPitBoostLap: null,
  raceConfig: {
    currentLap: 1,
    lapOffset: 0,
    lapTimeRace: null,
    lapTimeAttack: null,
    targetRace: null,
    targetAttack: null,
    attackScenario: 2,
    attackActivations: [],
    attacksRemainingLive: [],
    pitScenario: 3,
    targetPitSoc: 40,
    pitLap: null,
    dValue: null,
    pitWindowOpenEnergy: null,
    pitWindowCloseEnergy: null,
  },
  offsetPreviews: [],
  pitPreviews: [],
  optimisationTrigger: null,
});

// Helper to parse decoded channels array to record
const parseDecodedChannels = (
  channelsArray: { name: string; value: string }[],
): DecodedEncryptionChannels => {
  const channels: DecodedEncryptionChannels = {};
  for (const channel of channelsArray) {
    channels[channel.name] = channel.value;
  }
  return channels;
};

export const raceDataSlice = createSlice({
  name: "raceData",
  initialState,
  reducers: {
    setRaceData: (state, action) => {
      const raceData = action.payload;
      console.log(raceData);
      for (const carData of raceData) {
        const carColour = carData?.carColour;
        if (carColour && carData) {
          state[carColour] ??= createEmptyCarRaceData();
          state[carColour].ressPitBoostLap = carData.ressPitBoostLap ?? null;
          // If lapTable exists, update the state with the new data
          if (carData.lapTable) {
            const lapTable: LapTable = {};
            for (const lap of carData.lapTable) {
              lapTable[lap.lapNumber] = {
                lapNumber: lap.lapNumber,
                lapsRemaining: lap.lapsRemaining,
                energyEntry: lap.energyEntry,
                energy: lap.energy,
                greenCode: {
                  encryptionCode: lap.greenCode.encryptionCode,
                  decodedChannels: parseDecodedChannels(
                    lap.greenCode.decodedChannels,
                  ),
                },
                orangeCode: {
                  encryptionCode: lap.orangeCode.encryptionCode,
                  decodedChannels: parseDecodedChannels(
                    lap.orangeCode.decodedChannels,
                  ),
                },
                predictedEnergy: lap.predictedEnergy,
                target: lap.target,
                eLap: lap.eLap,
                lapTimeEntry: lap.lapTimeEntry,
                lapTime: lap.lapTime,
                predictedLapTime: lap.predictedLapTime,
                lapStartTime: lap.lapStartTime,
                SoC: lap.SoC,
                predictedSoC: lap.predictedSoC,
                pitInSoC: lap.pitInSoC,
                predictedPitInSoC: lap.predictedPitInSoC,
                tCellMax: lap.tCellMax ?? null,
                predictedTCellMax: lap.predictedTCellMax ?? null,
                activationLap: lap.activationLap,
                freezeLap: lap.freezeLap,
                attackMode: lap.attackMode,
                attackSegments: lap.attackSegments,
                pitScenario: lap.pitScenario,
                comment: lap.comment,
              };
            }
            state[carColour].lapTable = lapTable;
          }
          // If raceConfig exists, update the state with the new data
          if (carData.raceConfig) {
            state[carColour].raceConfig = {
              currentLap: carData.raceConfig.currentLap,
              lapOffset: carData.raceConfig.lapOffset,
              lapTimeRace: carData.raceConfig.lapTimeRace,
              lapTimeAttack: carData.raceConfig.lapTimeAttack,
              targetRace: carData.raceConfig.targetRace,
              targetAttack: carData.raceConfig.targetAttack,
              attackScenario: carData.raceConfig.attackScenario,
              attackActivations: carData.raceConfig.attackActivations,
              attacksRemainingLive: carData.raceConfig.attacksRemainingLive,
              pitScenario: carData.raceConfig.pitScenario,
              targetPitSoc: carData.raceConfig.targetPitSoc ?? 40,
              pitLap: carData.raceConfig.pitLap,
              dValue: carData.raceConfig.dValue,
              pitWindowOpenEnergy: carData.raceConfig.pitWindowOpenEnergy,
              pitWindowCloseEnergy: carData.raceConfig.pitWindowCloseEnergy,
            };
          }
          if (carData.offsetPreviews) {
            state[carColour].offsetPreviews = carData.offsetPreviews;
          }
          if (carData.pitPreviews) {
            state[carColour].pitPreviews = carData.pitPreviews;
          }
          if (carData.optimisationTrigger) {
            state[carColour].optimisationTrigger = carData.optimisationTrigger;
          }
        }
      }
    },
    updateLap: (state, action) => {
      const { carColour, lap } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      const lapNumber = lap.lapNumber;

      state[carColour].lapTable[lapNumber] = {
        lapNumber,
        lapsRemaining: lap.lapsRemaining,
        energyEntry: lap.energyEntry,
        energy: lap.energy,
        greenCode: {
          encryptionCode: lap.greenCode.encryptionCode,
          decodedChannels: parseDecodedChannels(lap.greenCode.decodedChannels),
        },
        orangeCode: {
          encryptionCode: lap.orangeCode.encryptionCode,
          decodedChannels: parseDecodedChannels(lap.orangeCode.decodedChannels),
        },
        predictedEnergy: lap.predictedEnergy,
        target: lap.target,
        eLap: lap.eLap,
        lapTimeEntry: lap.lapTimeEntry,
        lapTime: lap.lapTime,
        predictedLapTime: lap.predictedLapTime,
        lapStartTime: lap.lapStartTime,
        SoC: lap.SoC ?? null,
        predictedSoC: lap.predictedSoC ?? null,
        pitInSoC: lap.pitInSoC ?? null,
        predictedPitInSoC: lap.predictedPitInSoC ?? null,
        tCellMax: lap.tCellMax ?? null,
        predictedTCellMax: lap.predictedTCellMax ?? null,
        activationLap: lap.activationLap,
        freezeLap: lap.freezeLap,
        attackMode: lap.attackMode,
        attackSegments: lap.attackSegments,
        pitScenario: lap.pitScenario,
        comment: lap.comment,
      };
    },
    updateCarRaceConfig: (state, action) => {
      const { carColour, raceConfig } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      state[carColour].raceConfig = {
        currentLap: raceConfig.currentLap,
        lapOffset: raceConfig.lapOffset,
        lapTimeRace: raceConfig.lapTimeRace,
        lapTimeAttack: raceConfig.lapTimeAttack,
        targetRace: raceConfig.targetRace,
        targetAttack: raceConfig.targetAttack,
        attackScenario: raceConfig.attackScenario,
        attackActivations: raceConfig.attackActivations,
        attacksRemainingLive: raceConfig.attacksRemainingLive,
        pitScenario: raceConfig.pitScenario,
        targetPitSoc: raceConfig.targetPitSoc ?? 40,
        pitLap: raceConfig.pitLap,
        dValue: raceConfig.dValue,
        pitWindowOpenEnergy: raceConfig.pitWindowOpenEnergy,
        pitWindowCloseEnergy: raceConfig.pitWindowCloseEnergy,
      };
    },
    updateCarOffsetPreviews: (state, action) => {
      const { carColour, offsetPreviews } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      state[carColour].offsetPreviews = offsetPreviews;
    },
    updateCarPitPreviews: (state, action) => {
      const { carColour, pitPreviews } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      state[carColour].pitPreviews = pitPreviews;
    },
    updateCarOptimisationTrigger: (state, action) => {
      const { carColour, optimisationTrigger } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      state[carColour].optimisationTrigger = optimisationTrigger;
    },
    updateCarLapTable: (state, action) => {
      const { carColour, lapTable: lapTableArray } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      state[carColour].ressPitBoostLap = action.payload.ressPitBoostLap ?? null;
      const lapTable: LapTable = {};
      for (const lap of lapTableArray) {
        lapTable[lap.lapNumber] = {
          lapNumber: lap.lapNumber,
          lapsRemaining: lap.lapsRemaining,
          energyEntry: lap.energyEntry,
          energy: lap.energy,
          greenCode: {
            encryptionCode: lap.greenCode.encryptionCode,
            decodedChannels: parseDecodedChannels(
              lap.greenCode.decodedChannels,
            ),
          },
          orangeCode: {
            encryptionCode: lap.orangeCode.encryptionCode,
            decodedChannels: parseDecodedChannels(
              lap.orangeCode.decodedChannels,
            ),
          },
          predictedEnergy: lap.predictedEnergy,
          target: lap.target,
          eLap: lap.eLap,
          lapTimeEntry: lap.lapTimeEntry,
          lapTime: lap.lapTime,
          predictedLapTime: lap.predictedLapTime,
          lapStartTime: lap.lapStartTime,
          SoC: lap.SoC ?? null,
          predictedSoC: lap.predictedSoC ?? null,
          pitInSoC: lap.pitInSoC ?? null,
          predictedPitInSoC: lap.predictedPitInSoC ?? null,
          tCellMax: lap.tCellMax ?? null,
          predictedTCellMax: lap.predictedTCellMax ?? null,
          activationLap: lap.activationLap,
          freezeLap: lap.freezeLap,
          attackMode: lap.attackMode,
          attackSegments: lap.attackSegments,
          pitScenario: lap.pitScenario,
          comment: lap.comment,
        };
      }
      state[carColour].lapTable = lapTable;
    },
    setLapTimeEntryLocal: (state, action) => {
      const { carColour, lapNumber, lapTimeEntry } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      const lap = state[carColour].lapTable[lapNumber];
      if (lap) {
        lap.lapTimeEntry = lapTimeEntry;
      }
    },
    setLapEnergyEntryLocal: (state, action) => {
      const { carColour, lapNumber, energyEntry } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      const lap = state[carColour].lapTable[lapNumber];
      if (lap) {
        lap.energyEntry = energyEntry;
      }
    },
    setLapCommentLocal: (state, action) => {
      const { carColour, lapNumber, comment } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      const lap = state[carColour].lapTable[lapNumber];
      if (lap) {
        lap.comment = comment;
      }
    },
    setLapEncryptionCodeLocal: (state, action) => {
      const {
        carColour,
        lapNumber,
        encryptionCode,
        encryptionColour,
        decodedChannels,
      } = action.payload;
      state[carColour] ??= createEmptyCarRaceData();
      const codeKey = encryptionColour === "Green" ? "greenCode" : "orangeCode";
      state[carColour].lapTable[lapNumber][codeKey] = {
        encryptionCode,
        decodedChannels: decodedChannels || {},
      };
      const lap = state[carColour].lapTable[lapNumber];
      const getTCellMaxFromCode = (code: typeof lap.greenCode) => {
        const value = code.decodedChannels.TCellMax;
        if (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "")
        ) {
          return null;
        }
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue : null;
      };
      lap.tCellMax =
        getTCellMaxFromCode(lap.greenCode) ??
        getTCellMaxFromCode(lap.orangeCode);
    },
  },
});

export const {
  setRaceData,
  updateLap,
  updateCarRaceConfig,
  updateCarOffsetPreviews,
  updateCarPitPreviews,
  updateCarOptimisationTrigger,
  updateCarLapTable,
  setLapEncryptionCodeLocal,
  setLapTimeEntryLocal,
  setLapEnergyEntryLocal,
  setLapCommentLocal,
} = raceDataSlice.actions;
export default raceDataSlice.reducer;
